import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../img/logo.png';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Fallback scroll detection for active sections (only on homepage)
      if (isHomePage) {
        const sections = document.querySelectorAll('section[id]');
        const scrollPosition = window.scrollY + window.innerHeight * 0.3; // 30% from top
        
        for (let i = sections.length - 1; i >= 0; i--) {
          const section = sections[i];
          if (section.offsetTop <= scrollPosition) {
            const sectionId = section.getAttribute('id');
            if (sectionId && sectionId !== activeSection) {
              console.log('Scroll fallback detected section:', sectionId);
              setActiveSection(sectionId);
            }
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage, activeSection]);

  // Track active section with intersection observer
  useEffect(() => {
    if (!isHomePage) return;

    const sections = document.querySelectorAll('section[id]');
    const observerOptions = {
      root: null,
      rootMargin: '-50px 0px -50px 0px', // More balanced trigger zones
      threshold: [0.1, 0.5] // Multiple thresholds for better detection
    };

    const observer = new IntersectionObserver((entries) => {
      // Sort entries by their intersection ratio and position
      const visibleEntries = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => {
          // Prioritize entries with higher intersection ratio
          if (b.intersectionRatio !== a.intersectionRatio) {
            return b.intersectionRatio - a.intersectionRatio;
          }
          // If ratios are equal, prioritize the one higher on the page
          return a.target.offsetTop - b.target.offsetTop;
        });

      if (visibleEntries.length > 0) {
        const mostVisible = visibleEntries[0];
        setActiveSection(mostVisible.target.id);
        
        // Debug log to see what's being detected
        console.log('Active section detected:', mostVisible.target.id, 'Ratio:', mostVisible.intersectionRatio);
      }
    }, observerOptions);

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, [isHomePage]);

  // Set active section based on current page
  useEffect(() => {
    if (location.pathname === '/news') {
      setActiveSection('news');
    } else if (location.pathname === '/contact') {
      setActiveSection('contact');
    } else if (isHomePage) {
      setActiveSection('home');
    }
  }, [location.pathname, isHomePage]);

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Helper function to get navigation item styling (using mobile style for desktop)
  const getNavItemClass = (sectionName, isActive) => {
    const baseClasses = "px-4 xl:px-6 py-2 rounded-lg transition-all text-sm xl:text-base font-medium font-mono tracking-wide";
    
    if (isActive) {
      return `${baseClasses} text-ninsun-gold bg-ninsun-gold/10 border border-ninsun-gold/30`;
    } else {
      return `${baseClasses} text-ninsun-text hover:text-ninsun-gold hover:bg-gray-100`;
    }
  };

  // Helper function for mobile menu item styling
  const getMobileNavItemClass = (sectionName, isActive) => {
    const baseClasses = "py-3 px-4 rounded-lg font-medium transition-colors font-mono tracking-wide";
    
    if (isActive) {
      return `${baseClasses} bg-ninsun-gold/10 text-ninsun-gold border-l-4 border-ninsun-gold`;
    } else {
      return `${baseClasses} text-ninsun-text hover:text-ninsun-gold hover:bg-gray-100`;
    }
  };

  return (
    <motion.nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link 
            to="/" 
            className="flex items-center space-x-3"
            onClick={(e) => {
              if (isHomePage) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg flex items-center justify-center overflow-hidden border border-gray-100 shadow-sm">
              <img src={logo} alt="Ninsun Biotech Logo" className="w-full h-full object-cover"/>
            </div>
            <span className="text-2xl md:text-3xl font-bold font-display tracking-tight text-[#D4AF37]">Ninsun Biotech</span>
          </Link>
          
          {/* Desktop Navigation - Hidden on smaller screens */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-8">
            {isHomePage ? (
              <>
                <a href="#home" className={getNavItemClass('home', activeSection === 'home')}>Home</a>
                <a href="#technology" className={getNavItemClass('technology', activeSection === 'technology')}>Technology</a>
                <a href="#clinical" className={`${getNavItemClass('clinical', activeSection === 'clinical')} text-center leading-tight`}>Clinical<br />Validation</a>
                <a href="#workflow" className={getNavItemClass('workflow', activeSection === 'workflow')}>Routine Assay</a>
                <a href="#therapeutics" className={`${getNavItemClass('therapeutics', activeSection === 'therapeutics')} text-center leading-tight`}>Drug<br />Development</a>
                <a href="#team" className={getNavItemClass('team', activeSection === 'team')}>Team</a>
                <Link to="/news" className={getNavItemClass('news', activeSection === 'news')}>News</Link>
              </>
            ) : (
              <>
                <Link to="/" className={getNavItemClass('home', location.pathname === '/')}>Home</Link>
                <Link to="/#technology" className={getNavItemClass('technology', false)}>Technology</Link>
                <Link to="/#clinical" className={`${getNavItemClass('clinical', false)} text-center leading-tight`}>Clinical<br />Validation</Link>
                <Link to="/#workflow" className={getNavItemClass('workflow', false)}>Routine Assay</Link>
                <Link to="/#therapeutics" className={`${getNavItemClass('therapeutics', false)} text-center leading-tight`}>Drug<br />Development</Link>
                <Link to="/#team" className={getNavItemClass('team', false)}>Team</Link>
                <Link to="/news" className={getNavItemClass('news', location.pathname === '/news')}>News</Link>
              </>
            )}
            <Link to="/contact" className={`px-4 xl:px-6 py-2 rounded-lg transition-all text-sm xl:text-base font-semibold font-mono tracking-wide border ${
              location.pathname === '/contact' || activeSection === 'contact'
                ? 'bg-ninsun-gold text-white border-ninsun-gold'
                : 'text-ninsun-gold border-ninsun-gold hover:bg-ninsun-gold hover:text-white'
            }`}>
              Contact
            </Link>
          </div>

          {/* Hamburger Menu Button - Shows on screens smaller than lg (1024px) */}
          <button 
            className="lg:hidden text-ninsun-text p-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={isMenuOpen}
          >
            <motion.div
              animate={{ rotate: isMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.div>
          </button>
        </div>

        {/* Mobile Menu - Shows on screens smaller than lg (1024px) */}
        <motion.div 
          className={`lg:hidden overflow-hidden bg-white/95 backdrop-blur-md shadow-lg rounded-lg mt-4 border border-gray-200 ${isMenuOpen ? 'block' : 'hidden'}`}
          initial={{ opacity: 0, height: 0, y: -10 }}
          animate={{ 
            opacity: isMenuOpen ? 1 : 0, 
            height: isMenuOpen ? 'auto' : 0,
            y: isMenuOpen ? 0 : -10
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="flex flex-col space-y-1 py-4 px-4">
            {isHomePage ? (
              <>
                <a 
                  href="#home" 
                  className={getMobileNavItemClass('home', activeSection === 'home')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </a>
                <a
                  href="#technology"
                  className={getMobileNavItemClass('technology', activeSection === 'technology')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Technology
                </a>
                <a
                  href="#clinical"
                  className={getMobileNavItemClass('clinical', activeSection === 'clinical')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Clinical Validation
                </a>
                <a
                  href="#workflow"
                  className={getMobileNavItemClass('workflow', activeSection === 'workflow')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Routine Assay
                </a>
                <a
                  href="#therapeutics"
                  className={getMobileNavItemClass('therapeutics', activeSection === 'therapeutics')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Drug Development
                </a>
                <a
                  href="#team"
                  className={getMobileNavItemClass('team', activeSection === 'team')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Team
                </a>
                <Link 
                  to="/news" 
                  className={getMobileNavItemClass('news', activeSection === 'news')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  News
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to="/" 
                  className={getMobileNavItemClass('home', location.pathname === '/')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/#technology"
                  className={getMobileNavItemClass('technology', false)}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Technology
                </Link>
                <Link
                  to="/#clinical"
                  className={getMobileNavItemClass('clinical', false)}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Clinical Validation
                </Link>
                <Link
                  to="/#workflow"
                  className={getMobileNavItemClass('workflow', false)}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Routine Assay
                </Link>
                <Link
                  to="/#therapeutics"
                  className={getMobileNavItemClass('therapeutics', false)}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Drug Development
                </Link>
                <Link
                  to="/#team"
                  className={getMobileNavItemClass('team', false)}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Team
                </Link>
                <Link 
                  to="/news" 
                  className={getMobileNavItemClass('news', location.pathname === '/news')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  News
                </Link>
              </>
            )}
            <div className="border-t border-white/10 mt-2 pt-2">
              <Link 
                to="/contact" 
                className={`px-6 py-3 rounded-lg text-center font-semibold font-mono tracking-wide transition-all mt-2 block border ${
                  location.pathname === '/contact' || activeSection === 'contact'
                    ? 'bg-ninsun-gold text-white border-ninsun-gold'
                    : 'text-ninsun-gold border-ninsun-gold hover:bg-ninsun-gold hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >


                Contact Us
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navigation;